import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PolotnoEditor from "../components/PolotnoEditor";
import { getTemplateById, saveTemplate } from "../api/templates";
import { createTemplateAsset, getTemplateAssets } from "../api/templateAssets";
import { getAssetLibraryItems } from "../api/assetLibrary";
import { getEventById, saveEvent } from "../api/events";
import { useAuth } from "../contexts/AuthContext";
import { getApiBaseUrl } from "../api/utils";
import "./EditorPage.css";

const API_URL = getApiBaseUrl();
const delay = (ms) => new Promise((res) => setTimeout(res, ms));
const HEADER_COLLAPSE_STORAGE_KEY = "editorHeaderCollapsed";

const EVENT_TITLE_DEFAULTS = {
  wedding: "Wedding Celebration",
  birthday: "Birthday Celebration",
  baby_shower: "Baby Shower Celebration",
  graduation: "Graduation Celebration",
  housewarming: "Housewarming Celebration",
  engagement: "Engagement Celebration",
  corporate: "Corporate Event",
  cultural_programs: "Cultural Program",
};

const getDefaultEventTitle = (template) => {
  const eventType = String(template?.event_type || "")
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");

  return EVENT_TITLE_DEFAULTS[eventType] || "Celebration Event";
};

function downloadJsonFile(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function extractAndUploadMedia(store) {
  const rawJson = store.toJSON();
  const json = structuredClone
    ? structuredClone(rawJson)
    : JSON.parse(JSON.stringify(rawJson));

  async function traverse(node, callback) {
    if (!node) return;
    if (Array.isArray(node)) {
      for (const n of node) await traverse(n, callback);
      return;
    }

    await callback(node);

    if (Array.isArray(node.pages)) await traverse(node.pages, callback);
    if (Array.isArray(node.children)) await traverse(node.children, callback);
  }

  async function uploadMedia(base64Src) {
    if (!base64Src || typeof base64Src !== "string" || !base64Src.startsWith("data:")) {
      return base64Src;
    }

    try {
      const blob = await (await fetch(base64Src)).blob();
      const ext = blob.type.split("/")[1]?.split("+")[0] || "bin";

      const formData = new FormData();
      formData.append("file", blob, `media.${ext}`);

      const res = await fetch(`${API_URL}/media/polotno_upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error(`Upload failed: ${res.statusText}`);
      const data = await res.json();

      return data.file_url || data.image_url || data.media_url || base64Src;
    } catch (err) {
      console.error("Media upload failed:", err);
      return base64Src;
    }
  }

  async function processMediaObject(obj) {
    if (!obj || typeof obj !== "object") return;

    if (obj.src && obj.src.startsWith("data:")) {
      obj.src = await uploadMedia(obj.src);
    }
    if (obj.url && obj.url.startsWith("data:")) {
      obj.url = await uploadMedia(obj.url);
    }
    if (obj.preview && obj.preview.startsWith("data:")) {
      obj.preview = await uploadMedia(obj.preview);
    }
  }

  await traverse(json, async (el) => {
    if (!el || typeof el !== "object") return;

    const mediaTypes = ["image", "video", "sound", "audio", "svg"];
    if (mediaTypes.includes(el.type)) {
      await processMediaObject(el);
    }
  });

  if (Array.isArray(json.audios)) {
    for (const audio of json.audios) {
      await processMediaObject(audio);
    }
  }

  if (Array.isArray(json.videos)) {
    for (const video of json.videos) {
      await processMediaObject(video);
    }
  }

  return json;
}

async function exportAndUploadTemplateThumbnail(store) {
  const orderedPageIds = (store.toJSON()?.pages || []).map((page) => page.id);
  const firstPageId = orderedPageIds[0];

  if (!firstPageId) {
    return null;
  }

  try {
    await store.selectPage(firstPageId);
    await delay(200);

    const dataUrl = await store.toDataURL({ pageId: firstPageId });
    const blob = await (await fetch(dataUrl)).blob();
    const formData = new FormData();
    formData.append("file", blob, `${firstPageId}.png`);

    const res = await fetch(`${API_URL}/media/polotno_upload`, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) {
      throw new Error(`Thumbnail upload failed: ${res.statusText}`);
    }

    const data = await res.json();
    return data.file_url || data.image_url || data.media_url || null;
  } catch (error) {
    console.error("Template thumbnail export failed:", error);
    return null;
  }
}

async function saveEventWithSubEvents({ json, template, eventId, store, existingEvent = null }) {
  const json1 = store.toJSON();
  const orderedPageIds = json1.pages.map((p) => p.id);
  const pages = store.pages
    .slice()
    .sort((a, b) => orderedPageIds.indexOf(a.id) - orderedPageIds.indexOf(b.id));

  const thumbnails = [];

  for (const page of pages) {
    try {
      await store.selectPage(page.id);
      await delay(250);

      const dataUrl = await store.toDataURL({ pageId: page.id });
      const blob = await (await fetch(dataUrl)).blob();
      const formData = new FormData();
      formData.append("file", blob, `${page.id}.png`);

      const res = await fetch(`${API_URL}/media/polotno_upload`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.statusText}`);
      const data = await res.json();
      thumbnails.push({ page_id: page.id, file_url: data.file_url });
    } catch (err) {
      console.error("Failed to export page:", page.id, err);
    }
  }

  const existingSubevents = Array.isArray(existingEvent?.subevents) ? existingEvent.subevents : [];
  const findExistingSubevent = (page, index) =>
    existingSubevents.find((subevent) => subevent.page_id && subevent.page_id === page.id) ||
    existingSubevents.find((subevent) => Number(subevent.page_index) === index);

  const subevents = pages.slice(1).map((page, i) => {
    const pageIndex = i + 1;
    const existingSubevent = findExistingSubevent(page, pageIndex);

    return {
      ...(existingSubevent || {}),
      title: existingSubevent?.title || `Subevent ${i + 1}`,
      htmltext: existingSubevent?.htmltext || "",
      image_url: thumbnails[i + 1]?.file_url || existingSubevent?.image_url,
      page_id: thumbnails[i + 1]?.page_id || page.id || existingSubevent?.page_id,
      page_index: pageIndex,
      event_date: existingSubevent?.event_date || existingEvent?.event_date || new Date(),
      event_time: existingSubevent?.event_time || "",
      location: existingSubevent?.location || "",
    };
  });

  const normalizedEventTypeName = (template?.event_type || "")
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");

  const mainEvent = {
    ...(existingEvent || {}),
    title: existingEvent?.title || getDefaultEventTitle(template),
    event_date: existingEvent?.event_date || new Date(),
    event_time: existingEvent?.event_time || "",
    location: existingEvent?.location || "",
    host_name: existingEvent?.host_name || "",
    event_type_id: existingEvent?.event_type_id || null,
    event_type_name: existingEvent?.event_type_name || normalizedEventTypeName || null,
    template_id: existingEvent?.template_id || template?.id || null,
    htmltext: JSON.stringify(json),
    image_url: thumbnails[0]?.file_url || existingEvent?.image_url,
    page_id: thumbnails[0]?.page_id || pages[0]?.id || existingEvent?.page_id,
    page_index: 0,
    subevents,
  };

  const savedEvent = await saveEvent({ mainEvent, eventId });

  return {
    event: savedEvent,
    thumbnails,
  };
}

export default function EditorPage() {
  const { templateId, eventId: routeEventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionMode =
    typeof window !== "undefined" && templateId
      ? window.sessionStorage.getItem(`template-editor-mode:${templateId}`)
      : null;
  const sessionEventId =
    typeof window !== "undefined" && templateId
      ? window.sessionStorage.getItem(`template-editor-event:${templateId}`)
      : null;
  const stateEventId = location.state?.eventId;
  const mode = location.state?.mode || sessionMode || "template";
  useEffect(() => {
    if (typeof window === "undefined" || !templateId || !location.state?.mode) {
      return;
    }
    window.sessionStorage.setItem(`template-editor-mode:${templateId}`, location.state.mode);
    if (stateEventId) {
      window.sessionStorage.setItem(`template-editor-event:${templateId}`, stateEventId);
    }
  }, [location.state?.mode, stateEventId, templateId]);

  const [sessionDraftJson] = useState(() => {
    if (typeof window === "undefined" || !templateId) {
      return null;
    }

    const stored = window.sessionStorage.getItem(`template-editor-draft:${templateId}`);
    if (!stored) {
      return null;
    }

    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error("Failed to parse session draft JSON:", error);
      return null;
    }
  });
  const draftJson = location.state?.jsonData || sessionDraftJson || null;
  const [sessionTemplateMetadata] = useState(() => {
    if (typeof window === "undefined" || !templateId) {
      return null;
    }

    const stored = window.sessionStorage.getItem(`template-editor-template:${templateId}`);
    if (!stored) {
      return null;
    }

    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error("Failed to parse session template metadata:", error);
      return null;
    }
  });
  const [sessionPendingTemplateAssets] = useState(() => {
    if (typeof window === "undefined" || !templateId) {
      return [];
    }

    const stored = window.sessionStorage.getItem(`template-editor-pending-assets:${templateId}`);
    if (!stored) {
      return [];
    }

    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error("Failed to parse pending template assets:", error);
      return [];
    }
  });
  const activeEventId = stateEventId || routeEventId || sessionEventId;

  const [template, setTemplate] = useState(null);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [eventDraftJson, setEventDraftJson] = useState(null);
  const [isEventLoading, setIsEventLoading] = useState(false);
  const [templateAssets, setTemplateAssets] = useState([]);
  const [assetLibraryItems, setAssetLibraryItems] = useState([]);
  const [store, setStore] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.sessionStorage.getItem(HEADER_COLLAPSE_STORAGE_KEY) === "true";
  });
  const { isAuthenticated, user } = useAuth();
  const isAdminTemplateMode = mode === "template" && Boolean(user?.is_admin);

  useEffect(() => {
    if (mode !== "event" || !activeEventId) {
      setEventToEdit(null);
      setEventDraftJson(null);
      setIsEventLoading(false);
      return;
    }

    let isMounted = true;

    async function loadEventDesign() {
      try {
        setIsEventLoading(true);
        const eventData = await getEventById(activeEventId);
        if (!isMounted) return;

        setEventToEdit(eventData);

        if (eventData?.htmltext) {
          try {
            setEventDraftJson(JSON.parse(eventData.htmltext));
          } catch (parseError) {
            console.error("Failed to parse event Polotno JSON:", parseError);
            setEventDraftJson(null);
          }
        } else {
          setEventDraftJson(null);
        }
      } catch (error) {
        console.error("Failed to load event design:", error);
        if (isMounted) {
          setEventToEdit(null);
          setEventDraftJson(null);
        }
      } finally {
        if (isMounted) {
          setIsEventLoading(false);
        }
      }
    }

    loadEventDesign();

    return () => {
      isMounted = false;
    };
  }, [activeEventId, mode]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.sessionStorage.setItem(HEADER_COLLAPSE_STORAGE_KEY, String(isHeaderCollapsed));
  }, [isHeaderCollapsed]);

  useEffect(() => {
    async function loadTemplate() {
      if (sessionTemplateMetadata) {
        setTemplate(sessionTemplateMetadata);
        return;
      }

      const tpl = await getTemplateById(templateId);
      setTemplate(tpl);
    }
    loadTemplate();
  }, [sessionTemplateMetadata, templateId]);

  useEffect(() => {
    if (!templateId || !isAdminTemplateMode) {
      setTemplateAssets([]);
      setAssetLibraryItems([]);
      return;
    }

    let isMounted = true;

    async function loadTemplateAssets() {
      if (!template?.id) {
        const libraryResult = await getAssetLibraryItems({ limit: 200 });
        if (isMounted) {
          setTemplateAssets([]);
          setAssetLibraryItems(libraryResult || []);
        }
        return;
      }

      const [assetsResult, libraryResult] = await Promise.allSettled([
        getTemplateAssets(templateId),
        getAssetLibraryItems({ limit: 200 }),
      ]);

      if (!isMounted) {
        return;
      }

      if (assetsResult.status === "fulfilled") {
        setTemplateAssets(assetsResult.value || []);
      } else {
        console.error("Unable to load template assets:", assetsResult.reason);
        setTemplateAssets([]);
      }

      if (libraryResult.status === "fulfilled") {
        setAssetLibraryItems(libraryResult.value || []);
      } else {
        console.error("Unable to load asset library:", libraryResult.reason);
        setAssetLibraryItems([]);
      }
    }

    loadTemplateAssets();

    return () => {
      isMounted = false;
    };
  }, [isAdminTemplateMode, template?.id, templateId]);

  useEffect(() => {
    if (typeof window === "undefined" || !templateId || !sessionDraftJson || sessionTemplateMetadata) {
      return;
    }

    window.sessionStorage.removeItem(`template-editor-draft:${templateId}`);
    window.sessionStorage.removeItem(`template-editor-mode:${templateId}`);
  }, [sessionDraftJson, sessionTemplateMetadata, templateId]);

  const handleSave = async ({ navigateToPreview = true } = {}) => {
    if (!store || isSaving) {
      return;
    }

    if (!isAuthenticated) {
      navigate("/login", { state: { from: `/editor/${templateId}`, mode } });
      return;
    }

    setIsSaving(true);
    try {
      const json = await extractAndUploadMedia(store);
      let event;

      if (mode === "template") {
        const thumbnail = await exportAndUploadTemplateThumbnail(store);
        const savedTemplate = await saveTemplate({ template, json, thumbnail });
        setTemplate(savedTemplate);

        if (!template?.id && sessionPendingTemplateAssets.length) {
          try {
            await Promise.all(
              sessionPendingTemplateAssets.map((assetPayload) =>
                createTemplateAsset(savedTemplate.id, assetPayload)
              )
            );
            window.sessionStorage.removeItem(`template-editor-pending-assets:${templateId}`);
          } catch (assetError) {
            console.error("Failed to seed template asset plan:", assetError);
          }
        }

        if (typeof window !== "undefined") {
          window.sessionStorage.removeItem(`template-editor-template:${templateId}`);
          window.sessionStorage.removeItem(`template-editor-draft:${templateId}`);
          window.sessionStorage.removeItem(`template-editor-mode:${templateId}`);
          window.sessionStorage.removeItem(`template-editor-event:${templateId}`);
        }

        if (navigateToPreview) {
          navigate(`/preview/${savedTemplate.id}`, {
            state: { jsonData: json, templateId: savedTemplate.id, mode },
          });
        }
      } else if (mode === "event") {
        const saveResult = await saveEventWithSubEvents({
          json,
          template,
          eventId: activeEventId,
          store,
          existingEvent: eventToEdit,
        });
        event = saveResult?.event;
        setEventToEdit(event);
        if (navigateToPreview) {
          navigate(`/preview/${event?.id}`, {
            state: {
              jsonData: json,
              templateId,
              eventId: event?.id,
              mode,
              slideshowImages: (saveResult?.thumbnails || [])
                .map((item) => item.file_url)
                .filter(Boolean),
            },
          });
        }
      }
    } catch (error) {
      console.error("Save and preview failed:", error);
      window.alert(error?.message || "Unable to save and preview right now.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadDebugJson = () => {
    if (!store) {
      return;
    }

    const json = store.toJSON();
    const safeName = String(template?.name || templateId || "polotno-template")
      .trim()
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();
    const filename = `${safeName || "polotno-template"}-debug.json`;

    console.info("[EditorPage] Downloading live Polotno JSON", {
      filename,
      pages: Array.isArray(json.pages) ? json.pages.length : 0,
      width: json.width,
      height: json.height,
      json,
    });
    downloadJsonFile(json, filename);
  };

  if (!template || isEventLoading) {
    return (
      <div className="editor-page-loading">
        <div className="editor-loading-card">
          <p className="editor-page-kicker">Design Studio</p>
          <h1>Loading your editor...</h1>
        </div>
      </div>
    );
  }

  const pageCount = store?.pages?.length || 0;
  const stageLabel = mode === "event" ? "Event Build" : "Template Studio";
  const studioNote =
    mode === "event"
      ? "Design the invitation pages here, then preview and finalise the event details."
      : "Refine the base template here, then preview the slideshow or video before publishing.";
  const saveButtonLabel =
    mode === "template"
      ? isSaving
        ? "Saving Template..."
        : "Save Template & Preview"
      : isSaving
        ? "Saving..."
        : "Save & Preview";

  return (
    <div className="editor-page">
      <div className={`editor-page-shell${isHeaderCollapsed ? " editor-page-shell-collapsed" : ""}`}>
        <header className={`editor-page-header${isHeaderCollapsed ? " editor-page-header-collapsed" : ""}`}>
          <div className="editor-page-header-copy">
            <p className="editor-page-kicker">{stageLabel}</p>
            <h1>{template?.name || "Invitation Editor"}</h1>
            {!isHeaderCollapsed ? <p>{studioNote}</p> : null}
          </div>

          <div className="editor-page-header-actions">
            <button
              type="button"
              className="editor-collapse-btn"
              onClick={() => setIsHeaderCollapsed((value) => !value)}
              aria-label={isHeaderCollapsed ? "Expand editor header" : "Collapse editor header"}
              title={isHeaderCollapsed ? "Expand editor header" : "Collapse editor header"}
            >
              <span className={`editor-collapse-icon${isHeaderCollapsed ? " editor-collapse-icon-collapsed" : ""}`}>
                ^
              </span>
            </button>
            <button
              type="button"
              className="editor-secondary-btn"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
            {mode === "template" ? (
              <button
                type="button"
                className="editor-secondary-btn"
                onClick={() => handleSave({ navigateToPreview: false })}
                disabled={!store || isSaving}
              >
                {isSaving ? "Saving Template..." : "Save Template"}
              </button>
            ) : null}
            <button
              type="button"
              className="editor-secondary-btn"
              onClick={handleDownloadDebugJson}
              disabled={!store}
              title="Download the live Polotno JSON for debugging"
            >
              Download JSON
            </button>
            <button
              type="button"
              className="editor-primary-btn"
              onClick={() => handleSave({ navigateToPreview: true })}
              disabled={!store || isSaving}
            >
              {saveButtonLabel}
            </button>
          </div>
        </header>

        {!isHeaderCollapsed ? (
          <section className="editor-page-summary-grid">
            <div className="editor-summary-card">
              <span className="editor-summary-label">Mode</span>
              <strong>{mode === "event" ? "Event Invitation" : "Template Design"}</strong>
            </div>
            <div className="editor-summary-card">
              <span className="editor-summary-label">Pages</span>
              <strong>{pageCount || "Loading..."}</strong>
            </div>
            <div className="editor-summary-card">
              <span className="editor-summary-label">Coming Next</span>
              <strong>AI-assisted edits</strong>
            </div>
          </section>
        ) : null}

        <section className={`editor-stage${isHeaderCollapsed ? " editor-stage-expanded" : ""}`}>
          <div className={`editor-stage-hint${isHeaderCollapsed ? " editor-stage-hint-collapsed" : ""}`}>
            <span>Design Canvas</span>
            {!isHeaderCollapsed ? (
              <p>
                This shell keeps the Polotno editing experience intact while leaving room for future
                AI customization controls.
              </p>
            ) : null}
          </div>

          <div className="editor-canvas-shell">
            <PolotnoEditor
              template={template}
              initialJson={draftJson || eventDraftJson}
              onStoreReady={setStore}
              templateAssets={templateAssets}
              assetLibraryItems={assetLibraryItems}
              showTemplateAssetsPanel={isAdminTemplateMode}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
